import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function usePortfolioRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/portfolio/rules");
      setRules(res.data?.rules || res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const createRule = useCallback(async (data) => {
    try {
      await api.post("/portfolio/rules", data);
      toast.success("Portfolio rule created");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create rule");
      return false;
    }
  }, [refetch]);

  const updateRule = useCallback(async (id, data) => {
    try {
      await api.put(`/portfolio/rules/${id}`, data);
      toast.success("Portfolio rule updated");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update rule");
      return false;
    }
  }, [refetch]);

  const deleteRule = useCallback(async (id) => {
    try {
      await api.delete(`/portfolio/rules/${id}`);
      toast.success("Portfolio rule deleted");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete rule");
      return false;
    }
  }, [refetch]);

  const toggleRule = useCallback(async (id) => {
    try {
      const rule = rules.find((r) => r.id === id);
      if (!rule) return;
      await api.put(`/portfolio/rules/${id}`, { enabled: !rule.enabled });
      toast.success(rule.enabled ? "Rule disabled" : "Rule enabled");
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle rule");
    }
  }, [rules, refetch]);

  const testRule = useCallback(async (id) => {
    try {
      const res = await api.post(`/portfolio/rules/${id}/test`);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
      throw e;
    }
  }, []);

  return {
    rules, loading, error, refetch,
    createRule, updateRule, deleteRule, toggleRule, testRule,
  };
}
